import { UISref } from "@uirouter/react";
import moment from "moment";
import { FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "../../../../components/button";
import { Icon } from "../../../../components/icons";
import { KycIcon } from "../../../../components/icons/status";
import { Loader } from "../../../../components/loader";
import { Table } from "../../../../components/table";
import { TableHeaders } from "../../../../components/table/Head";
import { Widget } from "../../../../components/widget";
import { Pageable } from "../../../../services/pageable";
import { AppStore } from "../../../../store";
import { useFetchable } from "../../../../store/common/fetchable";
import { validationFields } from "../../../../store/common/models/fields";
import { VerificationActions } from "../../../../store/verification/actions";
import { VerificationDTO } from "../../../../store/verification/models/dto/aml";
import { Verification } from "../../../../store/verification/reducer";
import { colours } from "../../../../styles/colours";
import { FraudRoutes } from "../../routes";
import { NoBusinessAml } from "./empty";

type SingleBusinessAml = VerificationDTO.Responses.GetSingleBusinessAml.T;
type fieldNames = keyof SingleBusinessAml;

function useHeaders(): TableHeaders<fieldNames> {
  const companyName = validationFields.companyName.useName();
  const amlStatus = validationFields.amlStatus.useName();
  const createdDate = validationFields.createdDate.useName();
  return useMemo(
    () => [
      {
        name: "companyName",
        title: companyName,
      },
      {
        name: "status",
        title: amlStatus,
      },
      {
        name: "createdDate",
        title: createdDate,
      },
      {
        name: "fraudCheckId",
        title: "",
        centered: true,
      },
    ],
    [companyName, amlStatus, createdDate]
  );
}

export const AllBusinessAml = () => {
  const data = useFetchable(
    Verification.allBusinessAml,
    useMemo<Pageable.T<keyof VerificationDTO.Responses.GetSingleBusinessAml.T>>(
      () => ({
        pagination: {
          size: 20,
          page: 0,
        },
        sorting: {
          direction: "desc",
          field: "createdDate",
        },
      }),
      []
    )
  );

  return (
    <Loader data={[data]}>
      {([fulfilled]) => (
        <PresentationalAllBusinessAml businessAmlList={fulfilled} />
      )}
    </Loader>
  );
};

const PresentationalAllBusinessAml: FunctionComponent<{
  businessAmlList: VerificationDTO.Responses.GetAllBusinessAml.T;
}> = ({ businessAmlList }) => {
  const dispatch = useDispatch();

  const pageableState = useSelector<
    AppStore,
    AppStore["verification"]["allBusinessAml"]["pagination"]
  >((store) => store.verification.allBusinessAml.pagination);

  const onPaginationChange = (x: Pageable.T<fieldNames>) =>
    dispatch(VerificationActions.setAllBusinessAmlPagination(x));

  const headers = useHeaders();

  return (
    <Widget
      title="All Business AMLs"
      adornment={
        <UISref to={FraudRoutes.names.newBusinessAml}>
          <a>
            <PrimaryButton>
              <Icon type="plus" />
            </PrimaryButton>
          </a>
        </UISref>
      }
    >
      {businessAmlList.pageable.totalElements < 1 ? (
        <NoBusinessAml />
      ) : (
        <Table
          data={businessAmlList.content}
          head={headers}
          renderFields={{
            fraudCheckId: ({ fraudCheckId }) => (
              <UISref
                to={FraudRoutes.names.singleBusinessAml}
                params={{ fraudCheckId }}
              >
                <a>
                  <PrimaryButton variant="contained">
                    <Icon
                      type="export"
                      rotateAmount={270}
                      colour1={colours.white}
                    />
                  </PrimaryButton>
                </a>
              </UISref>
            ),
            createdDate: ({ createdDate }) => (
              <>{moment.utc(createdDate).local().format("yyy-MM-DD HH:mm")}</>
            ),
            status: ({ status }) => KycIcon(status, "aml"),
          }}
          pagination={businessAmlList.pageable}
          onPaginationChange={onPaginationChange}
          pageableState={pageableState}
        />
      )}
    </Widget>
  );
};
